import React, { useEffect, useState } from 'react';
import Footer from '../components/Footer';
import SiteNavigation from '../components/SiteNavigation';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://formsubmit.co/ajax/contact@1medicine.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          _subject: `[1Medicine Contact] ${formData.subject}`
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="site-canvas">
      <SiteNavigation currentPage="contact" />

      <main>
        <header className="border-b border-slate-200/80 pt-16">
          <div className="site-container py-10 sm:py-12">
            <p className="section-kicker">Contact</p>
            <h1 className="section-title max-w-2xl">How can we help?</h1>
            <p className="section-copy mt-3 max-w-2xl">
              Have questions? We'd love to hear from you.
            </p>
          </div>
        </header>

        <div className="site-container py-10 sm:py-14">
          <div className="grid items-start gap-8 lg:grid-cols-[0.8fr_1.2fr]">
            <aside aria-labelledby="contact-details-heading">
              <h2 id="contact-details-heading" className="text-xl font-semibold tracking-tight text-slate-900">
                Get in touch
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Reach our team directly or send a note using the secure form.
              </p>

              <div className="mt-5 space-y-3">
                <div className="surface-card flex gap-4 p-5">
                  <span className="soft-icon" aria-hidden="true">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </span>
                  <div>
                    <h3 className="font-semibold text-slate-900">Email</h3>
                    <a href="mailto:contact@1medicine.app" className="mt-1 inline-block text-sm font-medium text-emerald-700 hover:text-emerald-800 hover:underline">
                      contact@1medicine.app
                    </a>
                  </div>
                </div>

                <div className="surface-card flex gap-4 p-5">
                  <span className="soft-icon" aria-hidden="true">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                  <div>
                    <h3 className="font-semibold text-slate-900">Response time</h3>
                    <p className="mt-1 text-sm leading-6 text-slate-600">We typically respond within 24-48 hours</p>
                  </div>
                </div>

              </div>
            </aside>

            <section className="surface-card p-6 sm:p-8" aria-labelledby="contact-form-heading">
              <h2 id="contact-form-heading" className="text-xl font-semibold tracking-tight text-slate-900">
                Send us a message
              </h2>
              <p className="mt-2 text-sm text-slate-600">All fields are required.</p>

              {submitStatus === 'success' && (
                <div role="status" className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-800">
                  Thank you! Your message has been sent successfully.
                </div>
              )}

              {submitStatus === 'error' && (
                <div role="alert" className="mt-5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-800">
                  Something went wrong. Please try again or email us directly.
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-2 block text-sm font-medium text-slate-800">Your name</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required autoComplete="name" className="form-control" placeholder="John Doe" />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-800">Email address</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required autoComplete="email" className="form-control" placeholder="john@example.com" />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="mb-2 block text-sm font-medium text-slate-800">Subject</label>
                  <select id="subject" name="subject" value={formData.subject} onChange={handleChange} required className="form-control cursor-pointer">
                    <option value="">Select a subject</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Technical Support">Technical Support</option>
                    <option value="Billing Question">Billing Question</option>
                    <option value="Feature Request">Feature Request</option>
                    <option value="Partnership">Partnership Opportunity</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="mb-2 block text-sm font-medium text-slate-800">Message</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={6} className="form-control resize-y" placeholder="Tell us how we can help you..." />
                </div>

                <button type="submit" disabled={isSubmitting} className="primary-button w-full disabled:cursor-not-allowed disabled:opacity-60" aria-busy={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <svg className="h-4 w-4 animate-spin" aria-hidden="true" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-30" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" />
                        <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send message
                      <span aria-hidden="true">→</span>
                    </>
                  )}
                </button>
              </form>

              <p className="mt-5 text-center text-xs leading-5 text-slate-500">
                By submitting this form, you agree to our{' '}
                <a href="/privacy-policy" className="font-medium text-emerald-700 hover:underline">Privacy Policy</a>.
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;

